import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { BaseError } from 'helpers/BaseError';

import personalizationKeys from 'hooks/personalization/_queryKeys';

import { VariantCreateBulk } from 'models/variant.model';

import API from '../../services/api';
// services
import { toastService } from '../../services/toast.service';

// queryKeys
import alternateKeys from '../alternates/_queryKeys';
import formulaKeys from '../formulas/_queryKeys';
import messageContentQueryKeys from '../message-content/_queryKey';
import useGetCurrentMessageContent from '../message-content/useGetCurrentMessageContent';

const useVariantBulkCreateMutation = (
    formulaId: number,
    componentId?: string,
) => {
    const queryClient = useQueryClient();
    const { currentMessageContentId } = useGetCurrentMessageContent(formulaId);
    const messageKey = formulaKeys.formulaMessages(
        formulaId,
        currentMessageContentId,
    );

    return useMutation<AxiosResponse, BaseError, VariantCreateBulk[]>({
        mutationFn: (data: VariantCreateBulk[]) => API.createVariantBulk(data),

        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: alternateKeys.getAllVariants(),
            });
            if (componentId) {
                queryClient.invalidateQueries({
                    queryKey:
                        alternateKeys.getVariantsByComponentId(componentId),
                });
            }

            queryClient.invalidateQueries({
                queryKey: ['personalization-map'],
            });
            queryClient.invalidateQueries({
                queryKey: messageKey,
            });
            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaMessageCountUpdate(formulaId),
            });
            queryClient.invalidateQueries({
                queryKey: personalizationKeys.getHeroRecommendations(),
            });
            if (currentMessageContentId) {
                queryClient.invalidateQueries({
                    queryKey: messageContentQueryKeys.messageContent(
                        currentMessageContentId,
                    ),
                });
                queryClient.invalidateQueries({
                    queryKey: alternateKeys.getVariantsByMessageContentId(
                        currentMessageContentId,
                    ),
                });
            }
        },

        onSuccess: () => {
            toastService.showSuccess('Success: Alternates created.');
        },

        onError: () => {
            toastService.showError('Error: Failed to create alternates.');
        },
    });
};

export default useVariantBulkCreateMutation;
